/**
 * Named date-range windows for the task list.
 *
 * The window itself is resolved by the API, in the *workspace's* timezone —
 * see api/app/utils/date_ranges.py. The browser sends a preset name and gets
 * back the instants it resolved to. It deliberately does not compute the
 * window: the browser's timezone belongs to whoever is holding the laptop, and
 * an admin reviewing a Singapore workspace from Europe must see Singapore's
 * Monday. Deriving it here a second time is how the two would disagree.
 */

export const DATE_RANGE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This week' },
  { value: 'last_week', label: 'Last week' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  // Unbounded, and the most useful of them: the search worth having is the one
  // where you cannot remember when it was. Free on the search backend, and
  // unaffordable on Firestore's offset pagination, which is why it is new.
  { value: 'all_time', label: 'All time' },
  { value: 'custom', label: 'Custom…' },
];

export const DEFAULT_PRESET = 'today';

export function isCustomPreset(preset) {
  return preset === 'custom';
}

function partsIn(epochSeconds, timeZone) {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone, day: 'numeric', month: 'short', year: 'numeric',
  });
  return fmt.formatToParts(new Date(epochSeconds * 1000)).reduce((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value;
    return acc;
  }, {});
}

/**
 * Human label for a window the API resolved.
 *
 * `end` is exclusive — the next window's start — so the last *included*
 * instant is a millisecond before it. Showing `end` directly would label a
 * Monday-to-Sunday week as "31 Aug – 7 Sep", claiming a day it excludes.
 */
export function describeWindow(window) {
  if (!window) return '';
  const { start, end, timezone } = window;
  if (start == null && end == null) return 'All time';

  const tz = timezone || 'UTC';
  const from = partsIn(start, tz);
  const to = partsIn(end - 1, tz);

  if (from.day === to.day && from.month === to.month && from.year === to.year) {
    return `${to.day} ${to.month} ${to.year}`;
  }
  return `${from.day} ${from.month} – ${to.day} ${to.month} ${to.year}`;
}


/**
 * A custom range's day as `YYYY-MM-DD`, read on the calendar the person
 * picked it on. `toISOString()` reads it in UTC, which moved a day picked or
 * prefilled before 08:00 in Singapore to the day before. The API reads the
 * day in the workspace's timezone (date_ranges.resolve_custom).
 */
export function localDayString(value) {
  // A saved view or a cache can hand back the day as text.
  const date = value instanceof Date ? value
    : (typeof value === 'string' && parseLocalDay(value)) || (value ? new Date(value) : null);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** `YYYY-MM-DD` as local midnight, or null. The inverse of localDayString. */
export function parseLocalDay(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  if (!m) return null;
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}
